import { cn } from '@/utilities/ui'
import * as React from 'react'

/**
 * ArrowButton - Unified arrow button component following CIVO design system
 *
 * @description Provides consistent arrow button styling across the application.
 * Simple API with required backgroundColor prop for clear, explicit styling.
 *
 * @example
 * // White background for CTAs
 * <ArrowButton direction="right" backgroundColor="white" />
 *
 * // Lime background for carousels
 * <ArrowButton direction="left" backgroundColor="lime" size="large" />
 *
 * // Black background for dark themes
 * <ArrowButton direction="right" backgroundColor="black" />
 *
 * // Transparent background with white border for dark backgrounds (e.g., Testimonials block)
 * <ArrowButton direction="left" backgroundColor="transparent" size="medium" variant="minimal" animated />
 *
 * // White background with black border for light backgrounds (e.g., Carousel TextUnder)
 * <ArrowButton direction="right" backgroundColor="white" size="medium" variant="minimal" animated className="border border-black" />
 */

export interface ArrowButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Arrow direction
   */
  direction: 'left' | 'right'
  /**
   * Background color - required for explicit styling
   */
  backgroundColor: 'white' | 'black' | 'lime' | 'transparent'
  /**
   * Button size
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Arrow style variant
   */
  variant?: 'default' | 'minimal'
  /**
   * Enable hover animation (arrow slides in direction)
   */
  animated?: boolean
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * When true, renders as div instead of button (for use inside other buttons)
   */
  asChild?: boolean
}

const ArrowButton = React.forwardRef<HTMLButtonElement | HTMLDivElement, ArrowButtonProps>(
  ({ className, backgroundColor, size = 'small', direction, variant = 'default', animated = false, asChild = false, ...props }, ref) => {
    // Get background and text color classes based on backgroundColor prop
    const getColorClasses = () => {
      switch (backgroundColor) {
        case 'white':
          return 'bg-white hover:bg-gray-100 text-black'
        case 'black':
          return 'bg-black hover:bg-gray-800 text-white'
        case 'lime':
          return 'bg-civo-lime hover:bg-[#00e865] text-black'
        case 'transparent':
          return 'bg-transparent hover:bg-white/20 text-white border border-white'
        default:
          return 'bg-civo-lime hover:bg-[#00e865] text-black'
      }
    }

    // Get size classes
    const getSizeClasses = () => {
      switch (size) {
        case 'large':
          return 'w-[52px] h-[52px]'
        case 'medium':
          return 'w-10 h-10'
        default:
          return 'w-8 h-8'
      }
    }

    const baseClasses = cn(
      'inline-flex items-center justify-center rounded-full transition-colors overflow-hidden',
      !asChild && 'focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
      animated && 'group',
      getColorClasses(),
      getSizeClasses(),
      className
    )

    // Create the SVG icon element
    const createIcon = (isMinimal: boolean) => {
      if (isMinimal) {
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cn(direction === 'right' && 'rotate-180')}
          >
            <path
              d="M11.334 8.00008L5.50065 8.00008M5.50065 8.00008L8.41732 10.9167M5.50065 8.00008L8.41732 5.08341"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )
      }
      return (
        <svg
          width={size === 'large' ? '15' : '12'}
          height={size === 'large' ? '16' : '13'}
          viewBox="0 0 15 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cn(direction === 'right' && 'rotate-180')}
        >
          <path
            d="M1 8L0.637932 7.65517L0.309525 8L0.637932 8.34483L1 8ZM14 8.5C14.2761 8.5 14.5 8.27614 14.5 8C14.5 7.72386 14.2761 7.5 14 7.5L14 8L14 8.5ZM7.66667 0.999999L7.3046 0.655171L0.637932 7.65517L1 8L1.36207 8.34483L8.02874 1.34483L7.66667 0.999999ZM1 8L0.637932 8.34483L7.3046 15.3448L7.66667 15L8.02874 14.6552L1.36207 7.65517L1 8ZM1 8L1 8.5L14 8.5L14 8L14 7.5L1 7.5L1 8Z"
            fill="currentColor"
          />
        </svg>
      )
    }

    const icon = createIcon(variant === 'minimal')

    // For animated buttons, use dual-element slide animation
    const arrowIcon = animated ? (
      <span className="relative block overflow-hidden">
        {/* First icon - visible by default, slides out on hover */}
        <span
          className={cn(
            'block transition-transform duration-300 ease-in-out',
            direction === 'left' ? 'group-hover:-translate-x-full' : 'group-hover:translate-x-full',
          )}
        >
          {icon}
        </span>
        {/* Second icon - hidden by default, slides in on hover */}
        <span
          className={cn(
            'absolute inset-0 block transition-transform duration-300 ease-in-out',
            direction === 'left'
              ? 'translate-x-full group-hover:translate-x-0'
              : '-translate-x-full group-hover:translate-x-0',
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      </span>
    ) : (
      icon
    )

    if (asChild) {
      return (
        <div
          className={baseClasses}
          ref={ref as React.Ref<HTMLDivElement>}
          aria-label={`${direction === 'left' ? 'Previous' : 'Next'}`}
        >
          {arrowIcon}
        </div>
      )
    }

    return (
      <button
        className={baseClasses}
        ref={ref as React.Ref<HTMLButtonElement>}
        aria-label={`${direction === 'left' ? 'Previous' : 'Next'}`}
        {...props}
      >
        {arrowIcon}
      </button>
    )
  },
)

ArrowButton.displayName = 'ArrowButton'

export { ArrowButton }